<template>
  <div class="d-flex flex-column align-items-center">
    <MailConfirm ref="mailConfirm" mail-type="submit" />
    <div
      v-if="recruitment"
      class="form-wrapper"
      style="max-width: 1200px; width: 100%"
    >
      <template v-if="!confirming">
        <span class="text-danger">入力ミスにご注意下さい</span>

        <JoinForm
          v-model:target-date="form.targetDate"
          :area="recruitment.area"
          :recruitment-type="recruitment.recruitmentType"
          :recruitment="recruitment"
          :errors="errors"
          disabled
          class="mt-3"
        />
        <hr />
        <Invest24UserForm
          v-model:last-name="form.lastName"
          v-model:first-name="form.firstName"
          v-model:last-name-kana="form.lastNameKana"
          v-model:first-name-kana="form.firstNameKana"
          v-model:birthday="form.birthday"
          v-model:phone-number="form.phoneNumber"
          v-model:work-type="form.workType"
          v-model:email="form.email"
          v-model:email-check="emailCheck"
          :recruitment-type="recruitment.recruitmentType"
          :errors="errors"
        />

        <div class="w-100 d-flex justify-content-center mt-3">
          <OpenSiteBackButton class="me-3" label="キャンセル" />
          <MoreReadButton
            class="ms-3"
            color="green"
            label="確認"
            button-type="submit"
            @click="confirm"
          />
        </div>
      </template>
      <template v-else>
        <JoinUserShow
          :join-form="form"
          :area="recruitment.area"
          :recruitment-type="recruitment.recruitmentType"
        />

        <div class="w-100 d-flex justify-content-center my-3">
          <OpenSiteBackButton
            class="me-3"
            label="修正"
            :click-command="() => (confirming = false)"
          />
          <MoreReadButton
            class="ms-3"
            color="green"
            label="送信"
            button-type="submit"
            @click="submit"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from "vue";
import { scrollToTop } from "/@/modules/ui";
import { gKana, gPhoneNumber } from "/@/modules/groomer";
import {
  useUser,
  useZodScheme,
  useRouterUtil,
  useRecruitment,
} from "/@/vue/composables";
import { MailConfirm } from "/@/vue/components/Layouts";
import {
  BasicButton,
  MoreReadButton,
  OpenSiteBackButton,
} from "/@/vue/components/Atom";
import { JoinForm } from "/@/vue/components/Organisms/Users/Invest24Recruitments";
import { Form as Invest24UserForm } from "/@/vue/components/Organisms/Invest24/Invest24Users";
import { Show as JoinUserShow } from "/@/vue/components/Molecules/JoinUser";
import {
  UserClient,
  Invest24JoinUserNewForm,
  Invest24JoinUserNewCheckScheme,
} from "/@/types";

// user

const { getUser } = useUser();
const { data: user } = getUser();

// confirm

const mailConfirm = ref();

const { backto, goto, currentRouteQuery } = useRouterUtil();

// query

const recruitmentId = computed<number>(() => {
  return Number(currentRouteQuery.value.id);
});

const targetDate = computed<string>(() => {
  return currentRouteQuery.value.targetDate;
});

// recruitment

const { getRecruitment, applyRecruitmentWithNewUser } = useRecruitment();
const { data: recruitment } = getRecruitment(recruitmentId);

// form

const { useFormAndErrors } = useZodScheme();
const { form, errors, startValidation } =
  useFormAndErrors<Invest24JoinUserNewForm>(Invest24JoinUserNewCheckScheme, {
    birthday: "1990-01-01",
    targetDate: targetDate.value,
    workType: "通勤",
  });

watch(form, (f) => {
  // form を watch する場合は、まとめて変更した時の処理を書けるが、
  // 無駄な比較処理が増えるのと過去の値へのアクセスができない問題がある。
  // そのため個別のプロパティでの変更があった時のような処理が書けない

  if (f.firstName && f.firstName.length > 50) {
    form.firstName = f.firstName.slice(0, 50);
  }

  if (f.lastName && f.lastName.length > 50) {
    form.lastName = f.lastName.slice(0, 50);
  }

  if (f.firstNameKana) {
    const kana = gKana(f.firstNameKana);
    form.firstNameKana = kana.slice(0, 50);
  }

  if (f.lastNameKana) {
    const kana = gKana(f.lastNameKana);
    form.lastNameKana = kana.slice(0, 50);
  }

  if (f.phoneNumber) {
    form.phoneNumber = gPhoneNumber(f.phoneNumber);
  }
});

const emailCheck = ref("");

function initByUser(u: UserClient) {
  if (u) {
    form.userId = u.id;
    form.firstName = u.firstName;
    form.lastName = u.lastName;
    form.firstNameKana = u.firstNameKana;
    form.lastNameKana = u.lastNameKana;
    form.birthday = u.birthday;
    form.phoneNumber = u.phoneNumber;
    form.email = u.email;

    emailCheck.value = u.email;
  }
}

watch(user, (u) => {
  if (u && !form.userId) {
    initByUser(u);
  }
});

onMounted(() => {
  if (user.value) {
    initByUser(user.value);
  }
});

/*
form.lastName = "試験";
form.firstName = "追加";
form.lastNameKana = "テスト";
form.firstNameKana = "ツイカ";
form.phoneNumber = "09012345678";
form.email = "s1200191@gmail.com";
emailCheck.value = "s1200191@gmail.com";
*/

startValidation.value = true;

const confirming = ref(false);
function confirm() {
  try {
    if (form.email !== emailCheck.value) {
      throw new Error("メールアドレスが一致しません。");
    }

    Invest24JoinUserNewCheckScheme.parse(form);
    scrollToTop();
    confirming.value = true;
  } catch (e) {
    console.error(e);
    window.alert("入力に問題があります。赤枠の項目を確認して下さい。");
  }
}

const loading = ref(false);

async function submit() {
  try {
    const rec = recruitment.value;
    if (!rec) {
      return;
    }

    loading.value = true;

    if (form.email !== emailCheck.value) {
      throw new Error("メールアドレスが一致しません。");
    }

    const prms = Invest24JoinUserNewCheckScheme.parse(form);

    if (await applyRecruitmentWithNewUser(recruitmentId.value, prms)) {
      mailConfirm.value.open(() => {
        if (rec.recruitmentType == "invest24") goto({ name: "Invest24Index" });
        if (rec.recruitmentType == "shelter24")
          goto({ name: "Shelter24Index" });
      });
    } else {
      window.alert("申請に失敗しました。すでに申請済みか、人数が一杯です");
    }
    return;
  } catch (e) {
    console.error(e);
    window.alert("入力に問題があります。赤枠の項目を確認して下さい。");
  }

  loading.value = false;
}
</script>

<style scoped>
.form-wrapper {
  min-height: 100vh;
}
</style>
